import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BatchPackageMatch } from 'returnCenter-api';
import ConfirmationDialog from 'components/ui/ConfirmationDialog';
import ActionButton from 'components/ui/ActionButton';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import { BatchPackagePrompt } from 'hooks/useBatchPackageScan';
import ReopenBatchDialog from 'components/batch-returns/ReopenBatchDialog';

type BatchPackageScanDialogsProps = {
    prompt: BatchPackagePrompt | null;
    busy: boolean;
    onChoose: (match: BatchPackageMatch) => void;
    /** The comment is the reason given for reopening a completed batch. */
    onConfirm: (reopenComment?: string) => void;
    onCancel: () => void;
};

const BatchPackageScanDialogs = ({ prompt, busy, onChoose, onConfirm, onCancel }: BatchPackageScanDialogsProps) => {
    const { t } = useTranslation();
    const batchId = prompt && prompt.kind !== 'choose' ? prompt.match.batchReturnId : undefined;

    return (
        <>
            <ConfirmationDialog
                open={prompt?.kind === 'receive'}
                onClose={onCancel}
                title={t('batchPackage.receiveTitle', 'Batch not received')}
                content={t(
                    'batchPackage.receiveMessage',
                    "This package belongs to batch #{{batchId}}, which hasn't been received yet. Mark the batch as received?",
                    { batchId },
                )}
                onCancel={onCancel}
                onConfirm={() => !busy && onConfirm()}
            />

            <ConfirmationDialog
                open={prompt?.kind === 'missing'}
                onClose={onCancel}
                title={t('batchPackage.missingTitle', 'Package marked missing')}
                content={t(
                    'batchPackage.missingMessage',
                    'This package is marked missing in batch #{{batchId}}. Undo that and create a return slip for it?',
                    { batchId },
                )}
                onCancel={onCancel}
                onConfirm={() => !busy && onConfirm()}
            />

            <ReopenBatchDialog
                open={prompt?.kind === 'reopen'}
                batchId={batchId}
                message={t(
                    'batchPackage.reopenMessage',
                    'Batch #{{batchId}} is completed, and this package was marked missing in it. Reopen the batch and take the package in?',
                    { batchId },
                )}
                defaultComment={
                    prompt?.kind === 'reopen'
                        ? t('batchPackage.reopenDefaultComment', 'Package #{{returnId}} turned up', {
                              returnId: prompt.match.sourceReturnId,
                          })
                        : ''
                }
                busy={busy}
                onCancel={onCancel}
                onConfirm={(comment) => !busy && onConfirm(comment)}
            />

            <Dialog open={prompt?.kind === 'choose'} onClose={onCancel}>
                <DialogTitle>{t('batchPackage.chooseTitle', 'Package found in several batches')}</DialogTitle>
                <DialogContent>
                    <Flex column gap="0.75rem">
                        <Text>{t('batchPackage.chooseMessage', 'Choose the batch to receive it in.')}</Text>
                        {prompt?.kind === 'choose' &&
                            prompt.matches.map((match) => (
                                <ActionButton
                                    key={`${match.batchReturnId}-${match.companyCode}`}
                                    label={t('batchPackage.batchOption', 'Batch #{{batchId}}', {
                                        batchId: match.batchReturnId,
                                    })}
                                    onClick={() => onChoose(match)}
                                    fullWidth
                                />
                            ))}
                    </Flex>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default BatchPackageScanDialogs;
